import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { BorderGradient } from 'processhub-components-lib';
import { FiX } from 'react-icons/fi';

interface NotificationFormData {
  name: string;
  description: string;
  notify: string;
  templateCategory: string;
  userRole: string;
}

interface AddNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (notificationData: NotificationFormData) => void;
  initialData?: NotificationFormData | null;
  isEdit?: boolean;
}

const AddNotificationModal: React.FC<AddNotificationModalProps> = ({ isOpen, onClose, onSave, initialData, isEdit }) => {
  const [formData, setFormData] = useState<NotificationFormData>({
    name: '',
    description: '',
    notify: 'Email',
    templateCategory: '',
    userRole: ''
  });

  useEffect(() => {
    if (initialData) setFormData(initialData);
    else setFormData({ name: '', description: '', notify: 'Email', templateCategory: '', userRole: '' });
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
    onClose();
    setFormData({
      name: '',
      description: '',
      notify: 'Email',
      templateCategory: '',
      userRole: ''
    });
  };

  const notifyOptions = ['Email', 'In-app'];
  const templateCategories = ['New Registration', 'System alerts', 'Subscriptions and plans'];
  const userRoles = ['Super admin', 'Billing admin', 'Support agent'];

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 z-50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90vw] lg:w-[1024px] h-[70vh]">
          <BorderGradient className="rounded-lg flex flex-col h-full w-full">
            <div className="bg-[#1A1A1A] rounded-lg p-6 relative text-white overflow-y-auto flex flex-col h-full">
              <button onClick={onClose} className="absolute top-[28px] right-4 text-gray-400 hover:text-white">
                <FiX size={24} />
              </button>

              <h2 className="text-lg font-semibold mb-6">{isEdit ? 'Edit Notification' : 'Add Notification'}</h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Notification Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-md px-3 py-2 text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-md px-3 py-2 text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Notify</label>
                  <select
                    value={formData.notify}
                    onChange={(e) => setFormData({ ...formData, notify: e.target.value })}
                    className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-md px-3 py-2 text-white"
                    required
                  >
                    {notifyOptions.map(opt => <option key={opt}>{opt}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Template Category</label>
                  <select
                    value={formData.templateCategory}
                    onChange={(e) => setFormData({ ...formData, templateCategory: e.target.value })}
                    className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-md px-3 py-2 text-white"
                    required
                  >
                    {templateCategories.map(cat => <option key={cat}>{cat}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">User Role</label>
                  <select
                    value={formData.userRole}
                    onChange={(e) => setFormData({ ...formData, userRole: e.target.value })}
                    className="w-full bg-[#2A2A2A] border border-white/[0.08] rounded-md px-3 py-2 text-white"
                    required
                  >
                    {userRoles.map(role => <option key={role}>{role}</option>)}
                  </select>
                </div>

                <div className="flex justify-end">
                  <button type="submit" className="flex items-cente justify-center gap-2 px-4 py-2 w-[140px] bg-[#2A2A2A] text-white rounded-lg hover:bg-[#3a3a3a33] transition-colors">
                    {isEdit ? 'Update' : 'Add'}
                  </button>
                </div>
              </form>
            </div>
          </BorderGradient>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default AddNotificationModal;
